import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useLocation,
  useNavigate,
  type NavigateFn,
} from "@tanstack/react-router";
import { DocumentsScreen } from "../features/documents/DocumentsScreen.js";
import { FocusScreen } from "../features/focus/FocusScreen.js";
import { GraphScreen } from "../features/graph/GraphScreen.js";
import { validateFocusSearch } from "../features/focus/searchState.js";
import { validateGraphSearch } from "../features/graph/searchState.js";
import { WorkScreen } from "../features/work/WorkScreen.js";
import { validateWorkSearch } from "../features/work/searchState.js";
import { validateDocumentsSearch } from "../features/documents/searchState.js";
import { SignInScreen, SetupScreen, SimulationScreen } from "../auth/index.js";
import { AppSessionProvider, useAppSession } from "./AppSession.js";
import { AppShell, BootScreen } from "./AppShell.js";
import { WorkspaceGate } from "./WorkspaceGate.js";
import {
  clearProtectedDestination,
  rememberProtectedDestination,
  takeProtectedDestination,
  type ProtectedDestination,
} from "./protectedDestination.js";
import { validateAnonymousSearch } from "./workspaceSearch.js";
import { resolveProtectedLayout } from "./protectedLayout.js";

function RootComponent() {
  return (
    <AppSessionProvider>
      <Outlet />
    </AppSessionProvider>
  );
}

function IndexRoute() {
  const session = useAppSession();
  if (session.phase === "booting") return <BootScreen />;
  if (session.phase === "error") {
    return <BootScreen error={session.error} onRetry={() => void session.retry()} />;
  }
  return <Navigate to={session.phase === "authenticated" ? "/focus" : "/sign-in"} replace />;
}

function ProtectedLayout() {
  const session = useAppSession();
  const location = useLocation();
  const mode = resolveProtectedLayout(
    session.phase,
    session.workspaces.length,
    (session.identity?.simulation ?? null) !== null,
  );

  if (mode === "boot") return <BootScreen />;
  if (mode === "error") {
    return <BootScreen error={session.error} onRetry={() => void session.retry()} />;
  }
  if (mode === "anonymous") {
    rememberProtectedDestination({ pathname: location.pathname, search: location.search });
    return <Navigate to="/sign-in" replace />;
  }
  if (mode === "workspace-gate" || mode === "simulation-gate") return <WorkspaceGate />;
  return <AppShell />;
}

type NavigateFunction = NavigateFn;

async function navigateToProtectedDestination(
  navigate: NavigateFunction,
  destination: ProtectedDestination,
): Promise<void> {
  switch (destination.to) {
    case "/focus":
      await navigate({ to: "/focus", search: destination.search, replace: true });
      return;
    case "/work":
      await navigate({ to: "/work", search: destination.search, replace: true });
      return;
    case "/graph":
      await navigate({ to: "/graph", search: destination.search, replace: true });
      return;
    case "/documents":
      await navigate({ to: "/documents", search: destination.search, replace: true });
  }
}

function SignInRoute() {
  const session = useAppSession();
  const navigate = useNavigate();
  if (session.phase === "booting") return <BootScreen />;
  if (session.phase === "authenticated") return <Navigate to="/focus" search={{}} replace />;
  return (
    <SignInScreen
      onAuthenticated={async () => {
        await session.restartIdentity();
        await navigateToProtectedDestination(navigate, takeProtectedDestination());
      }}
    />
  );
}

function SetupRoute() {
  const navigate = useNavigate();
  return (
    <SetupScreen
      onComplete={async () => {
        clearProtectedDestination();
        await navigate({ to: "/sign-in", replace: true });
      }}
    />
  );
}

function SimulationRoute() {
  const session = useAppSession();
  const navigate = useNavigate();
  return (
    <SimulationScreen
      onComplete={async () => {
        await session.restartIdentity();
        await navigateToProtectedDestination(navigate, takeProtectedDestination());
      }}
      onRejectIdentity={async () => {
        clearProtectedDestination();
        await navigate({ to: session.phase === "authenticated" ? "/focus" : "/sign-in", replace: true });
      }}
    />
  );
}

const rootRoute = createRootRoute({ component: RootComponent });
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: IndexRoute });
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: validateAnonymousSearch,
  component: SignInRoute,
});
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
  validateSearch: validateAnonymousSearch,
  component: SetupRoute,
});
const simulationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/simulate",
  validateSearch: validateAnonymousSearch,
  component: SimulationRoute,
});
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: ProtectedLayout,
});
const focusRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/focus",
  validateSearch: validateFocusSearch,
  component: FocusScreen,
});
const workRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/work",
  validateSearch: validateWorkSearch,
  component: WorkScreen,
});
const graphRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/graph",
  validateSearch: validateGraphSearch,
  component: GraphScreen,
});
const documentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/documents",
  validateSearch: validateDocumentsSearch,
  component: DocumentsScreen,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  setupRoute,
  simulationRoute,
  protectedRoute.addChildren([focusRoute, workRoute, graphRoute, documentsRoute]),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
